import { useEffect } from "react";
import { createPortal } from "react-dom";
import { ImageOff } from "lucide-react";

// Same rules as the Generation Gateway: JPEG, PNG, WebP or HEIC, up to 50 MB.
export const MAX_IMAGE_BYTES = 50 * 1024 * 1024;
const TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
const EXTENSIONS = ["jpg", "jpeg", "jfif", "png", "webp", "heic", "heif"];

export type FileProblem = { kind: "type" | "size"; name: string; detail: string };

/** Why a file can't be used, or null if it's fine. Some browsers give HEIC no type, so the extension counts too. */
export function checkImageFile(file: File): FileProblem | null {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!TYPES.includes(file.type) && !EXTENSIONS.includes(ext)) {
    return { kind: "type", name: file.name, detail: ext ? ext.toUpperCase() : file.type || "unknown" };
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return { kind: "size", name: file.name, detail: `${(Math.ceil((file.size / 1024 / 1024) * 10) / 10).toFixed(1)} MB` }; // rounded up, so just-over never reads "50.0"
  }
  return null;
}

interface UnsupportedFileModalProps {
  problem: FileProblem | null;
  onClose: () => void;
  onChooseAnother: () => void;
}

export function UnsupportedFileModal({ problem, onClose, onChooseAnother }: UnsupportedFileModalProps) {
  useEffect(() => {
    if (!problem) return;
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [problem, onClose]);

  if (!problem) return null;
  const tooBig = problem.kind === "size";

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="unsupported-file-title">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-[380px] max-w-[calc(100vw-32px)] rounded-2xl border border-white/10 shadow-2xl shadow-black/40 p-6 animate-modal-in"
        style={{ background: "linear-gradient(180deg, #2a2035 0%, #1a1520 100%)" }}
      >
        <div className="flex flex-col items-center text-center">
          <div className="size-12 rounded-full bg-[#d4a574]/15 flex items-center justify-center mb-4">
            <ImageOff className="size-6 text-[#d4a574]" />
          </div>
          <h2 id="unsupported-file-title" className="text-lg font-bold text-foreground mb-2">
            {tooBig ? "This image is too large" : "This file type isn't supported"}
          </h2>
          <p className="text-[13px] text-muted-foreground leading-relaxed mb-4">
            <span className="text-foreground font-semibold break-all">{problem.name}</span>
            {tooBig ? ` is ${problem.detail}. Images can be up to 50 MB.` : ` is a ${problem.detail} file. Please upload a photo or image in one of the formats below.`}
          </p>
          <div className="w-full rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3 mb-6 text-[12px] text-muted-foreground">
            <span className="text-foreground font-semibold">JPEG, PNG, WebP or HEIC</span> · up to 50 MB
          </div>
          <div className="flex gap-3 w-full">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold btn-glass transition-colors">
              Cancel
            </button>
            <button onClick={onChooseAnother} className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold bg-[#d4a574] hover:bg-[#c99560] text-[#1a1520] transition-colors">
              Choose another
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
